import { useState, useEffect } from 'react';
import axios from 'axios';
import { Wave, Leaf, Spark } from '@/components/HypIcons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Curated wisdom library (static until CMS is live) ────────────────────
const WISDOM_LIBRARY = [
  {
    id: 'w1',
    category: 'Neuroplasticity',
    title: 'Your brain changes every time you pause.',
    body: 'Neuroplasticity is not a metaphor — it is a biological fact. Every moment of deliberate self-observation, every practice of returning attention to the present, physically reshapes the neural architecture of your prefrontal cortex. The act of noticing is already the transformation.',
    source: 'LeDoux, J. (2015). Anxious. Viking Press.',
    readTime: '90s'
  },
  {
    id: 'w2',
    category: 'Emotional Regulation',
    title: 'Regulation is not suppression.',
    body: 'Regulating an emotional state does not mean silencing it. It means widening your capacity to be with the signal without being consumed by it. The nervous system responds to presence, not control. The more you witness, the less you are driven.',
    source: 'Porges, S. (2011). The Polyvagal Theory. Norton.',
    readTime: '90s'
  },
  {
    id: 'w3',
    category: 'Integration',
    title: 'The space between stimulus and response.',
    body: 'Viktor Frankl wrote from experience: "Between stimulus and response there is a space. In that space is our power to choose our response." What we train in HYPSEYA is precisely that space — not the response, not the stimulus, but the interval of awareness between them.',
    source: 'Frankl, V. (1946). Man\'s Search for Meaning.',
    readTime: '90s'
  },
  {
    id: 'w4',
    category: 'Nervous System',
    title: 'Safety is the foundation of growth.',
    body: 'The nervous system does not differentiate between psychological and physical threat. When the body senses danger — real or imagined — it prioritises survival over learning. This is why emotional safety is not a luxury in personal development. It is the prerequisite.',
    source: 'Van der Kolk, B. (2014). The Body Keeps the Score. Viking.',
    readTime: '90s'
  },
  {
    id: 'w5',
    category: 'Coherence',
    title: 'Heart and mind are not separate systems.',
    body: 'The heart contains approximately 40,000 neurons and communicates directly with the brain. Heart Rate Variability — the subtle variation in your heartbeat — reflects the quality of the dialogue between these two intelligences. Coherence is not a feeling. It is a measurable state.',
    source: 'HeartMath Institute Research Library.',
    readTime: '90s'
  },
  {
    id: 'w6',
    category: 'Practice',
    title: 'Consistency outweighs intensity.',
    body: 'A single peak experience rarely produces lasting change. What transforms is the quiet accumulation of small moments of self-awareness — repeated, embodied, integrated. The nervous system learns through repetition, not revelation. Show up lightly. Show up often.',
    source: 'Schwartz, R. (1995). Internal Family Systems Model.',
    readTime: '90s'
  },
  {
    id: 'w7',
    category: 'Presence',
    title: 'The present moment is the only place change happens.',
    body: 'Memory is past interpretation. Projection is future anxiety. The only moment in which new patterns can be encoded in the nervous system is now — in the body, in the breath, in the felt sense of this instant. This is why practice is not preparation for life. Practice is life.',
    source: 'Siegel, D. (2010). Mindsight. Bantam Books.',
    readTime: '90s'
  }
];

// Pick today's wisdom based on day of year
function getTodaysWisdom() {
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  return WISDOM_LIBRARY[dayOfYear % WISDOM_LIBRARY.length];
}

// ── Mirror insight engine ─────────────────────────────────────────────────
function generateMirrorInsight(eriData, trainingProfile, journalEntries) {
  const insights = [];

  if (eriData && eriData.eri_score !== undefined) {
    const score = eriData.eri_score;
    if (score >= 75) {
      insights.push({
        label: 'Your ERI this period',
        value: `${score}/100`,
        text: 'Your regulation capacity is strong. This is a window to deepen practice — not maintain it.',
        tone: 'strong'
      });
    } else if (score >= 50) {
      insights.push({
        label: 'Your ERI this period',
        value: `${score}/100`,
        text: 'You are building. The nervous system needs repetition to consolidate. Keep showing up lightly.',
        tone: 'growing'
      });
    } else {
      insights.push({
        label: 'Your ERI this period',
        value: `${score}/100`,
        text: 'Early stage work is the most important. You are laying the foundation. There is nothing to rush.',
        tone: 'early'
      });
    }
  }

  if (trainingProfile && trainingProfile.current_streak > 2) {
    insights.push({
      label: 'Practice streak',
      value: `${trainingProfile.current_streak} days`,
      text: 'Continuity signals safety to the nervous system. Something is shifting, even when you cannot feel it.',
      tone: 'growing'
    });
  }

  if (insights.length === 0) {
    insights.push({
      label: 'Today',
      value: 'Begin',
      text: 'Your data builds as you practice. Each session teaches the system more about your unique patterns.',
      tone: 'early'
    });
  }

  return insights[0];
}

// ── Resonance pulse (anonymous presence counter) ──────────────────────────
function ResonancePulse({ count = 0 }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '12px', padding: '28px', textAlign: 'center'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {[...Array(Math.min(count, 7))].map((_, i) => (
          <div key={i} style={{
            width: '6px', height: '6px', borderRadius: '50%',
            background: `rgba(45, 139, 135, ${0.3 + i * 0.1})`,
            animation: `hyp-pulse ${1.5 + i * 0.2}s ease-in-out infinite`,
            animationDelay: `${i * 0.15}s`
          }} />
        ))}
      </div>
      <p style={{
        margin: 0, fontSize: '0.8rem', color: '#8FA3A0', letterSpacing: '0.04em',
        fontStyle: 'italic'
      }}>
        {count > 0
          ? `${count} people are inside today`
          : 'You are here. That is enough.'}
      </p>
    </div>
  );
}

// ── Category pill ──────────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  'Neuroplasticity': { bg: 'rgba(30,107,104,0.08)', text: '#1E6B68' },
  'Emotional Regulation': { bg: 'rgba(184,144,106,0.12)', text: '#8B6340' },
  'Integration': { bg: 'rgba(184,211,207,0.3)', text: '#2D6B68' },
  'Nervous System': { bg: 'rgba(74,172,168,0.1)', text: '#2D8B87' },
  'Coherence': { bg: 'rgba(201,168,76,0.12)', text: '#8B7340' },
  'Practice': { bg: 'rgba(30,107,104,0.06)', text: '#1E6B68' },
  'Presence': { bg: 'rgba(184,211,207,0.25)', text: '#1E6B68' },
};

// ─────────────────────────────────────────────────────────────────────────
export default function PsychedeliaLivingRoom() {
  const [user, setUser] = useState(null);
  const [eriData, setEriData] = useState(null);
  const [trainingProfile, setTrainingProfile] = useState(null);
  const [resonanceCount, setResonanceCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [savedWisdom, setSavedWisdom] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hyp_saved_wisdom') || '[]'); }
    catch { return []; }
  });
  const [resonated, setResonated] = useState(false);

  const wisdom = getTodaysWisdom();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try { setUser(JSON.parse(userData)); } catch {}
    }
    // Entrance animation
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;

    // Load ERI + training data
    const load = async () => {
      try {
        const [eriRes, profileRes] = await Promise.allSettled([
          axios.get(`${API}/eri/current/${user.user_id}`),
          axios.get(`${API}/training/profile/${user.user_id}`)
        ]);
        if (eriRes.status === 'fulfilled') setEriData(eriRes.value.data);
        if (profileRes.status === 'fulfilled') setTrainingProfile(profileRes.value.data);
      } catch {}
    };
    load();

    // Simulate resonance count (real: pull from backend analytics endpoint)
    const base = 12;
    const jitter = Math.floor(Math.random() * 8);
    setResonanceCount(base + jitter);

    // Mark today's visit
    const today = new Date().toISOString().split('T')[0];
    const visitKey = `hyp_inner_circle_visit_${today}`;
    if (!localStorage.getItem(visitKey)) {
      localStorage.setItem(visitKey, '1');
    }
  }, [user]);

  const mirrorInsight = generateMirrorInsight(eriData, trainingProfile, []);

  const toneColors = {
    strong:  { accent: '#1E6B68', bg: 'rgba(30,107,104,0.06)' },
    growing: { accent: '#2D8B87', bg: 'rgba(45,139,135,0.06)' },
    early:   { accent: '#B8906A', bg: 'rgba(184,144,106,0.06)' },
  };
  const toneColor = toneColors[mirrorInsight.tone] || toneColors.growing;
  const catColor  = CATEGORY_COLORS[wisdom.category] || { bg: 'rgba(30,107,104,0.08)', text: '#1E6B68' };

  const handleResonate = () => {
    if (!resonated) {
      setResonanceCount(c => c + 1);
      setResonated(true);
    }
  };

  const handleSave = () => {
    const already = savedWisdom.includes(wisdom.id);
    const updated = already
      ? savedWisdom.filter(id => id !== wisdom.id)
      : [...savedWisdom, wisdom.id];
    setSavedWisdom(updated);
    localStorage.setItem('hyp_saved_wisdom', JSON.stringify(updated));
  };

  const isSaved = savedWisdom.includes(wisdom.id);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.18) 100%)',
      padding: '32px 20px 100px',
      maxWidth: '680px',
      margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease'
    }}>

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginBottom: '40px', paddingTop: '8px' }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
          <Wave size={28} color="#2D8B87" />
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.8rem', color: '#1E6B68',
          margin: '0 0 8px', fontWeight: '500', letterSpacing: '-0.01em'
        }}>
          Inner Circle
        </h1>
        <p style={{
          margin: 0, color: '#8FA3A0', fontSize: '0.85rem',
          letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: '500'
        }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* ── Section 01: Your Mirror ───────────────────────────────────── */}
      <section style={{ marginBottom: '28px' }}>
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '28px',
          boxShadow: '0 2px 20px rgba(30,107,104,0.07)',
          borderLeft: `3px solid ${toneColor.accent}`
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            marginBottom: '16px'
          }}>
            <span style={{
              fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#8FA3A0', fontWeight: '600'
            }}>
              01 — Your Mirror
            </span>
          </div>

          <div style={{
            background: toneColor.bg, borderRadius: '12px',
            padding: '16px 20px', marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '0.72rem', color: toneColor.accent, letterSpacing: '0.06em',
              textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px'
            }}>
              {mirrorInsight.label}
            </div>
            <div style={{
              fontSize: '1.6rem', fontWeight: '700', color: toneColor.accent,
              fontFamily: "'Playfair Display', serif", lineHeight: 1
            }}>
              {mirrorInsight.value}
            </div>
          </div>

          <p style={{
            margin: 0, fontSize: '0.95rem', color: '#444',
            lineHeight: '1.7', fontStyle: 'italic'
          }}>
            "{mirrorInsight.text}"
          </p>
        </div>
      </section>

      {/* ── Section 02: Today's Depth ─────────────────────────────────── */}
      <section style={{ marginBottom: '28px' }}>
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '28px',
          boxShadow: '0 2px 20px rgba(30,107,104,0.07)'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <span style={{
              fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#8FA3A0', fontWeight: '600'
            }}>
              02 — Today's Depth
            </span>
            <span style={{
              fontSize: '0.7rem', color: '#B0BEC0', letterSpacing: '0.04em'
            }}>
              {wisdom.readTime} read
            </span>
          </div>

          {/* Category */}
          <div style={{ marginBottom: '14px' }}>
            <span style={{
              display: 'inline-block',
              background: catColor.bg, color: catColor.text,
              fontSize: '0.72rem', fontWeight: '600',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '4px 12px', borderRadius: '20px'
            }}>
              {wisdom.category}
            </span>
          </div>

          {/* Title */}
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.3rem', color: '#1E3A39',
            margin: '0 0 16px', fontWeight: '600', lineHeight: '1.4'
          }}>
            {wisdom.title}
          </h2>

          {/* Body */}
          <p style={{
            margin: '0 0 20px', fontSize: '0.95rem', color: '#444',
            lineHeight: '1.75'
          }}>
            {wisdom.body}
          </p>

          {/* Source */}
          <p style={{
            margin: '0 0 20px', fontSize: '0.78rem', color: '#AAB8B6',
            fontStyle: 'italic', borderTop: '1px solid rgba(184,211,207,0.3)',
            paddingTop: '14px'
          }}>
            {wisdom.source}
          </p>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Resonate */}
            <button
              onClick={handleResonate}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px',
                background: resonated ? 'rgba(30,107,104,0.08)' : 'transparent',
                border: `1px solid ${resonated ? '#1E6B68' : 'rgba(184,211,207,0.6)'}`,
                borderRadius: '20px', cursor: resonated ? 'default' : 'pointer',
                color: resonated ? '#1E6B68' : '#8FA3A0',
                fontSize: '0.82rem', fontWeight: '500',
                transition: 'all 0.25s ease'
              }}
            >
              <Wave size={14} color={resonated ? '#1E6B68' : '#B0BEC0'} />
              {resonated ? 'Resonated' : 'This resonates'}
            </button>

            {/* Save */}
            <button
              onClick={handleSave}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px',
                background: isSaved ? 'rgba(30,107,104,0.08)' : 'transparent',
                border: `1px solid ${isSaved ? '#1E6B68' : 'rgba(184,211,207,0.6)'}`,
                borderRadius: '20px', cursor: 'pointer',
                color: isSaved ? '#1E6B68' : '#8FA3A0',
                fontSize: '0.82rem', fontWeight: '500',
                transition: 'all 0.25s ease'
              }}
            >
              <Leaf size={14} color={isSaved ? '#1E6B68' : '#B0BEC0'} />
              {isSaved ? 'Saved' : 'Save'}
            </button>
          </div>
        </div>
      </section>

      {/* ── Section 03: Resonance ─────────────────────────────────────── */}
      <section style={{ marginBottom: '28px' }}>
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '20px 28px',
          boxShadow: '0 2px 20px rgba(30,107,104,0.07)'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={{
              fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#8FA3A0', fontWeight: '600'
            }}>
              03 — Presence
            </span>
          </div>
          <ResonancePulse count={resonanceCount} />
        </div>
      </section>

      {/* ── Archive hint ──────────────────────────────────────────────── */}
      {savedWisdom.length > 0 && (
        <div>
          <button
            onClick={() => setShowArchive(v => !v)}
            style={{
              width: '100%', textAlign: 'center', padding: '16px',
              background: 'rgba(30,107,104,0.04)', borderRadius: '16px',
              border: '1px solid rgba(184,211,207,0.3)',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
              <Spark size={16} color="#2D8B87" />
            </div>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#8FA3A0', fontStyle: 'italic' }}>
              {savedWisdom.length} insight{savedWisdom.length !== 1 ? 's' : ''} saved to your archive.
              <span style={{ marginLeft: '6px', color: '#2D8B87', fontStyle: 'normal' }}>
                {showArchive ? '↑ Hide' : '↓ View'}
              </span>
            </p>
          </button>

          {showArchive && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {WISDOM_LIBRARY.filter(w => savedWisdom.includes(w.id)).map(w => (
                <div key={w.id} style={{
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '14px',
                  padding: '16px 20px',
                  border: '1px solid rgba(184,211,207,0.35)'
                }}>
                  <div style={{
                    fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.06em',
                    color: '#2D8B87', textTransform: 'uppercase', marginBottom: '6px'
                  }}>
                    {w.category}
                  </div>
                  <p style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: '0.95rem', color: '#1E6B68',
                    margin: '0 0 8px', fontWeight: '400'
                  }}>
                    {w.title}
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: '#666', lineHeight: '1.6' }}>
                    {w.body}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#AAB8B6', fontStyle: 'italic' }}>
                    {w.source}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
